import React from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, ExportOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons';
import AssistantMarkdown from '../chat/AssistantMarkdown';
import PipelineTracePanel from '../chat/PipelineTracePanel';
import ChatMessageAvatar from '../chat/ChatMessageAvatar';
import ReplyQuoteBlock from './ReplyQuoteBlock';
import { displayMessageText } from './clusterMessages';
import { relativeTime } from '../../utils/relativeTime';
import { fullDateTime } from '../../utils/dateTimeFormat';
import { telegramMessageUrl } from '../../utils/telegramMessageUrl';

const { Text } = Typography;

function refuseLabel(reason) {
  if (reason === 'off_topic') return 'Off-topic';
  if (reason === 'no_data') return 'No data';
  return reason || 'Declined';
}

export default function ChatMessageBubble({
  msg,
  botName = 'Bot',
  assistantImg,
  showPipelineTrace,
  onQuoteClick,
  onDelete,
  platformChatId,
  platform = 'telegram_userbot',
}) {
  const isUser = msg.role === 'user';
  const isAssistant = msg.role === 'assistant';
  const isRefusalFeedback = msg.meta?.kind === 'refusal_feedback';
  const displayText = isUser ? displayMessageText(msg) : msg.content;
  const speaker = isUser
    ? (msg.meta?.sender_name || 'User')
    : botName;

  const telegramUrl =
    platform === 'telegram_userbot'
      ? telegramMessageUrl(platformChatId, msg.platform_message_id)
      : null;

  return (
    <div
      className={`messenger-message-row ${isUser ? 'messenger-message-row--user' : 'messenger-message-row--assistant'}`}
      data-message-id={msg.id}
    >
      <div className="messenger-message-avatar">
        {isUser ? (
          <ChatMessageAvatar icon={<UserOutlined />} bg="#3b82f6" size={30} />
        ) : (
          <ChatMessageAvatar src={assistantImg} icon={<RobotOutlined />} size={30} />
        )}
      </div>
      <div className="messenger-message-body">
        <Text type="secondary" className="messenger-message-speaker">
          {speaker}
        </Text>
        {showPipelineTrace && isAssistant && !isRefusalFeedback && msg.pipeline_trace && (
          <PipelineTracePanel variant="aboveBubble" trace={msg.pipeline_trace} />
        )}
        <div
          className={`messenger-bubble ${isUser ? 'messenger-bubble--user' : 'messenger-bubble--assistant'}`}
        >
          {isUser && msg.meta?.reply_to_text && (
            <ReplyQuoteBlock
              sender={msg.meta.reply_to_sender}
              text={msg.meta.reply_to_text}
              onPrimary
              onClick={
                onQuoteClick && msg.meta.reply_to_message_id
                  ? () => onQuoteClick(msg.meta.reply_to_message_id)
                  : undefined
              }
            />
          )}
          {isAssistant ? (
            <div className="chat-stream-markdown-wrap">
              <AssistantMarkdown content={displayText} />
            </div>
          ) : (
            <div className="messenger-bubble-text">{displayText}</div>
          )}
        </div>
        <div className="messenger-message-meta">
          {isUser && msg.answered === false && (
            <Tag color="orange" className="messenger-refused-tag">
              {refuseLabel(msg.refuse_reason)}
            </Tag>
          )}
          {isRefusalFeedback && (
            <Tag color="default">Decline feedback</Tag>
          )}
          <Tooltip title={fullDateTime(msg.created_at)}>
            <span className="messenger-message-time">{relativeTime(msg.created_at)}</span>
          </Tooltip>
          {telegramUrl && (
            <Tooltip title="Open in Telegram">
              <a
                href={telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="messenger-tg-link"
                onClick={(e) => e.stopPropagation()}
              >
                <ExportOutlined />
                <span>Telegram</span>
              </a>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Remove from history">
              <button
                type="button"
                className="messenger-message-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(msg);
                }}
              >
                <DeleteOutlined />
                <span>Delete</span>
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
